import { Box } from "@mui/material";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import AddButton from "../Components/Button/Add";
import ExportCsvButton from "../Components/Button/ExportCsvButton";
import ConfirmDialog from "../Components/Dialog/ConfirmDialog";
import SessionDialog from "../Components/Dialog/SessionDialog";
import Loading from "../Components/Loading";
import TestingSessionDataGrid from "../Components/TestingSessionDataGrid";
import { CTM_Colors, SessionGridRow } from "../Helper";
import pb from "../pocketBase";

export const MainPage = () => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [rows, setRows] = useState<SessionGridRow[]>([]);
    const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
    const [editingSession, setEditingSession] = useState<SessionGridRow | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(true);

    // Confirm dialog state
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [sessionToDelete, setSessionToDelete] = useState<{ id: string; name: string } | null>(null);

    useEffect(() => {
        let subscription: any;
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const [records, runs] = await Promise.all([
                    pb.collection('TestingSession').getFullList({ sort: 'date' }),
                    pb.collection('Run').getFullList({ fields: 'sessionId' }),
                ]);

                const runCounts: Record<string, number> = {};
                for (const run of runs) {
                    runCounts[run.sessionId] = (runCounts[run.sessionId] || 0) + 1;
                }

                const formattedRecords: SessionGridRow[] = records.reverse().map((record) => ({
                    id: record.id,
                    name: record.name,
                    info: record.info,
                    date: new Date(record.date),
                    runCount: runCounts[record.id] || 0,
                }));
                setRows(formattedRecords);
            } catch (error) {
                console.error('Error fetching data:', error);
                toast.error('Failed to load sessions');
            } finally {
                setIsLoading(false);
            }
        };
        const init = async () => {
            fetchData();
            subscription = await pb.collection('TestingSession').subscribe('*', () => {
                fetchData();
            });
        };

        init();

        return () => {
            if (subscription) {
                subscription();
            }
        };

    }, []);

    const handleOpenDialog = () => {
        setDialogMode('create');
        setEditingSession(undefined);
        setIsDialogOpen(true);
    }

    const handleEditSession = (session: SessionGridRow) => {
        setDialogMode('edit');
        setEditingSession(session);
        setIsDialogOpen(true);
    }

    const handleDeleteSession = (sessionId: string, sessionName: string) => {
        setSessionToDelete({ id: sessionId, name: sessionName });
        setConfirmDialogOpen(true);
    };

    const confirmDeleteSession = async () => {
        if (!sessionToDelete) return;

        try {
            await pb.collection('TestingSession').delete(sessionToDelete.id);
            setRows((prevRows) => prevRows.filter(row => row.id !== sessionToDelete.id));
            toast.success('Session deleted successfully!');
        } catch (error) {
            console.error('Error deleting session:', error);
            toast.error('Error deleting session');
        } finally {
            setConfirmDialogOpen(false);
            setSessionToDelete(null);
        }
    };

    const handleCloseDialog = () => {
        console.log('Close Dialog');
        setIsDialogOpen(false);
        setDialogMode('create');
        setEditingSession(undefined);
    }

    const handleFormSubmit = async (data: SessionGridRow) => {
        try {
            if (dialogMode === 'create') {
                // CREATE new session
                const record = await pb.collection('TestingSession').create(data);
                setRows((prevRows) => [...prevRows, { ...data, id: record.id }]);
                toast.success('Session created successfully!');
            } else {
                // UPDATE existing session
                await pb.collection('TestingSession').update(data.id, data);
                setRows((prevRows) => prevRows.map(row => row.id === data.id ? data : row));
                toast.success('Session updated successfully!');
            }
            setIsDialogOpen(false);
        } catch (error) {
            console.error('Error saving session:', error);
            toast.error('Error saving session');
        }
    };

    if (isLoading) {
        return <Loading message="Loading sessions..." />;
    }

    return (
        <div style={{ marginLeft: "20px", marginRight: "20px" }}>
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: 2,
                marginBottom: 2
            }}>
                <h1 style={{color: CTM_Colors.ctm_green}}>Testing Sessions</h1>
                <ExportCsvButton />
            </Box>
            <TestingSessionDataGrid
                rows={rows}
                onEdit={handleEditSession}
                onDelete={handleDeleteSession}
            />
            <SessionDialog
                open={isDialogOpen}
                onClose={handleCloseDialog}
                onSubmit={handleFormSubmit}
                mode={dialogMode}
                existingSession={editingSession}
            />
            <ConfirmDialog
                open={confirmDialogOpen}
                title="Delete Session"
                message={`Are you sure you want to delete "${sessionToDelete?.name}"? This will also delete all associated runs, lap times, and feedback.`}
                onConfirm={confirmDeleteSession}
                onCancel={() => setConfirmDialogOpen(false)}
                confirmText="Delete"
                cancelText="Cancel"
                confirmColor="error"
            />

            {/* needs to be the last element!!! */}
            <AddButton openDialog={handleOpenDialog} />
        </div>
    )
}

